import { useMemo } from 'react';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import {
  ClusteredPoint,
  clusterPoints,
  PointLike,
} from 'shared/utils/maps/clusterPoints';

const deduplicateMarkers = <T extends PointLike>(markers: T[]): T[] => {
  const seenIds = new Set<string>();
  const uniqueMarkers = markers.filter((marker) => {
    if (seenIds.has(marker.id)) {
      return false;
    }

    seenIds.add(marker.id);
    return true;
  });

  return uniqueMarkers.length === markers.length ? markers : uniqueMarkers;
};

const useClusteredMarkers = <T extends PointLike>(
  markers: T[],
  deriveExtraFields?: (p: T) => Record<string, unknown>,
): ClusteredPoint<T>[] => {
  const { isFeatureEnabled } = useStatsig();
  const isEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_MAP_CLUSTER_MINIMIZED_PILLS,
  );

  return useMemo(() => {
    if (!isEnabled) {
      const nativeMarkers = deriveExtraFields
        ? markers.map(
            (marker) =>
              ({
                ...marker,
                ...deriveExtraFields(marker),
              }) as ClusteredPoint<T>,
          )
        : (markers as ClusteredPoint<T>[]);

      return deduplicateMarkers(nativeMarkers);
    }

    return deduplicateMarkers(clusterPoints(markers, { deriveExtraFields }));
  }, [isEnabled, markers, deriveExtraFields]);
};

export default useClusteredMarkers;
